import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

export interface MobileNavLink {
  to: string;
  label: string;
  highlight?: boolean;
}

interface Props {
  links: MobileNavLink[];
  title?: string;
  footer?: ReactNode;
}

export default function MobileCogMenu({ links, title = 'Menu', footer }: Props) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const close = () => setOpen(false);

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="Open menu"
        onClick={() => setOpen(true)}
        sx={{
          bgcolor: 'rgba(0,200,83,0.15)',
          '&:hover': { bgcolor: 'rgba(0,200,83,0.3)' },
        }}
      >
        <MenuIcon />
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={close}
        slotProps={{
          paper: {
            sx: {
              width: 280,
              maxWidth: '85vw',
              background: 'linear-gradient(180deg, #0d1b2a 0%, #1b4332 100%)',
              color: '#fff',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
          <IconButton color="inherit" aria-label="Close menu" onClick={close} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
        <List sx={{ px: 1, py: 1 }}>
          {links.map((link) => {
            const active = location.pathname === link.to;
            return (
              <ListItemButton
                key={link.to}
                component={RouterLink}
                to={link.to}
                onClick={close}
                selected={active}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  fontWeight: active || link.highlight ? 800 : 600,
                  ...(link.highlight && {
                    bgcolor: 'rgba(0,200,83,0.2)',
                    '&:hover': { bgcolor: 'rgba(0,200,83,0.35)' },
                  }),
                  '&.Mui-selected': {
                    bgcolor: 'rgba(0,200,83,0.25)',
                    '&:hover': { bgcolor: 'rgba(0,200,83,0.35)' },
                  },
                }}
              >
                <ListItemText primary={link.label} />
              </ListItemButton>
            );
          })}
        </List>
        {footer && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
            <Box sx={{ p: 2 }}>{footer}</Box>
          </>
        )}
      </Drawer>
    </>
  );
}
